import { useEffect, useEffectEvent } from 'react';
import { Button } from '../ui/button';
import { ArrowUpToLine, Calculator, DollarSign, Edit3 } from 'lucide-react';
import { formatCurrency } from '../../utils/formatters';
import type {
  DrawdownCalculationResult,
  InvestorAllocation,
} from '../../services/drawdowns/drawdownCalculationService';

interface AllocationsStepProps {
  calculation: DrawdownCalculationResult | null;
  currency: string;
  isLoading: boolean;
  onCalculate: () => void;
  onEditAllocation?: (investorId: string) => void;
}

export function AllocationsStep({
  calculation,
  currency,
  isLoading,
  onCalculate,
  onEditAllocation,
}: AllocationsStepProps) {
  // Wrap onCalculate in useEffectEvent so it doesn't need to be a dependency
  const onCalculateEvent = useEffectEvent(() => {
    if (!calculation && !isLoading) {
      onCalculate();
    }
  });

  // Auto-calculate on mount if no calculation exists
  useEffect(() => {
    onCalculateEvent();
  }, []);

  if (!calculation) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <Calculator className="h-12 w-12 text-muted-foreground mb-4" />
        <h3 className="text-lg font-medium text-foreground mb-2">
          Calculating Allocations...
        </h3>
        <p className="text-muted-foreground mb-4">
          Please wait while we calculate the investor allocations
        </p>
        <Button onClick={onCalculate} disabled={isLoading}>
          {isLoading ? 'Calculating...' : 'Calculate Now'}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="!bg-blue-50 dark:!bg-blue-950 p-4 rounded-lg">
        <div className="flex items-center gap-2 mb-2">
          <DollarSign className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          <h4 className="font-medium text-blue-900 dark:text-blue-100">
            Calculation Summary
          </h4>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
          <div>
            <div className="text-blue-600 dark:text-blue-400">
              Total Call Amount
            </div>
            <div className="font-medium">
              {formatCurrency(calculation.total_gross_amount, { currency })}
            </div>
          </div>
          <div>
            <div className="text-amber-600 dark:text-amber-400">
              Prepayment Used
            </div>
            <div className="font-medium">
              {formatCurrency(calculation.total_prepayment_used, { currency })}
            </div>
          </div>
          <div>
            <div className="text-emerald-600 dark:text-emerald-400">
              Prepayment Contribution
            </div>
            <div className="font-medium">
              {formatCurrency(calculation.total_prepayment_contribution, {
                currency,
              })}
            </div>
          </div>
          <div>
            <div className="text-blue-600 dark:text-blue-400">Net Due</div>
            <div className="font-medium">
              {formatCurrency(calculation.total_net_amount, { currency })}
            </div>
          </div>
          <div>
            <div className="text-blue-600 dark:text-blue-400">Investors</div>
            <div className="font-medium">{calculation.allocations.length}</div>
          </div>
        </div>
      </div>

      <div className="border rounded-lg overflow-hidden" role="table">
        <div className="bg-muted px-4 py-2 border-b">
          <div className="grid grid-cols-7 gap-4 text-xs font-medium text-muted-foreground uppercase tracking-wide">
            <div>Investor</div>
            <div className="text-right">Allocation %</div>
            <div className="text-right">Gross Amount</div>
            <div className="text-right text-amber-700 dark:text-amber-300">
              Prepayment Used / Available
            </div>
            <div className="text-right text-emerald-700 dark:text-emerald-300">
              Prepayment Contribution
            </div>
            <div className="text-right">Net Due</div>
            <div className="text-center">Actions</div>
          </div>
        </div>
        <div className="max-h-[calc(100vh-36rem)] min-h-[14rem] overflow-y-scroll scrollbar-always">
          {calculation.allocations.map((allocation) => (
            <AllocationRow
              key={allocation.investor_id}
              allocation={allocation}
              currency={currency}
              onEditAllocation={onEditAllocation}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

interface AllocationRowProps {
  allocation: InvestorAllocation;
  currency: string;
  onEditAllocation?: (investorId: string) => void;
}

function AllocationRow({
  allocation,
  currency,
  onEditAllocation,
}: AllocationRowProps) {
  const prepaymentUsed = allocation.prepayment_used;
  const poolBalance = allocation.prepayment_pool_balance;
  const grossAmount = allocation.gross_amount;
  const prepaymentContribution = allocation.prepayment_contribution;
  const usedAtCap =
    prepaymentUsed > 0 &&
    poolBalance > 0 &&
    prepaymentUsed >= poolBalance &&
    poolBalance < grossAmount;

  return (
    <div
      className="px-4 py-3 border-b border-border hover:bg-muted/50"
      role="row"
    >
      <div className="grid grid-cols-7 gap-4 items-center">
        <div>
          <div className="font-medium text-sm">{allocation.investor_name}</div>
          {allocation.is_manual_override && (
            <div className="text-xs text-orange-600">Manual override</div>
          )}
        </div>
        <div className="text-right text-sm">
          {allocation.allocation_percentage.toFixed(2)}%
        </div>
        <div className="text-right text-sm">
          {formatCurrency(allocation.gross_amount, { currency })}
        </div>

        <div
          className="text-right text-sm whitespace-nowrap"
          title={usedAtCap ? 'Capped at available pool balance' : undefined}
        >
          <span
            className={
              prepaymentUsed > 0
                ? 'text-amber-700 dark:text-amber-300 font-medium'
                : 'text-muted-foreground'
            }
          >
            {formatCurrency(allocation.prepayment_used, { currency })}
          </span>
          <span className="text-muted-foreground">
            {' / '}
            {formatCurrency(allocation.prepayment_pool_balance, { currency })}
          </span>
        </div>

        <div
          className={`text-right text-sm ${
            prepaymentContribution > 0
              ? 'text-emerald-700 dark:text-emerald-300 font-medium'
              : 'text-muted-foreground'
          }`}
        >
          <span className="inline-flex items-center gap-1 justify-end">
            {prepaymentContribution > 0 && (
              <ArrowUpToLine className="h-3 w-3" aria-hidden />
            )}
            {formatCurrency(allocation.prepayment_contribution, { currency })}
          </span>
        </div>

        <div className="text-right text-sm">
          {formatCurrency(allocation.net_amount_due, { currency })}
        </div>
        <div className="text-center">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onEditAllocation?.(allocation.investor_id)}
          >
            <Edit3 className="h-3 w-3" />
          </Button>
        </div>
      </div>
    </div>
  );
}
